(function () {
  'use strict';

  if (typeof window === 'undefined' || !window.customElements || window.customElements.get('nl-phone-input')) return;

  function upgradeProperty(el, prop) {
    if (Object.prototype.hasOwnProperty.call(el, prop)) {
      var value = el[prop];
      delete el[prop];
      el[prop] = value;
    }
  }

  class NlPhoneInput extends HTMLElement {
    static get observedAttributes() {
      return ['aria-invalid', 'aria-describedby', 'aria-required', 'disabled', 'placeholder', 'name', 'autocomplete', 'inputmode', 'id'];
    }

    constructor() {
      super();
      this._value = '';
      this._built = false;
      upgradeProperty(this, 'value');
    }

    // The field id stays on the host (readControl/writeControl and every
    // getElementById(fieldId) caller expect the element carrying data-field to
    // be the one with .value), and the inner input mirrors it for native
    // input semantics — getElementById resolves to the host either way.
    get value() {
      return this._normalize(this._input ? this._input.value : this._value);
    }

    // The store echoes every keystroke straight back through writeControl, so
    // adopting the incoming text unconditionally would overwrite what the
    // seller is still typing with its own normalised form. Only take text that
    // says something new — a restored draft, a URL hydration, a reset.
    set value(next) {
      this._value = this._normalize(next == null ? '' : String(next));
      if (!this._input) return;
      if (this._normalize(this._input.value) === this._value) return;
      this._input.value = this._value;
    }

    connectedCallback() {
      if (this._built) return;
      this._built = true;
      this._render();
      this._bind();
      var self = this;
      NlPhoneInput.observedAttributes.forEach(function (name) {
        if (self.hasAttribute(name)) self._proxyAttribute(name, self.getAttribute(name));
      });
      this._input.value = this._value;
    }

    attributeChangedCallback(name, _oldValue, newValue) {
      this._proxyAttribute(name, newValue);
    }

    // The error-summary and focusFirstInvalid flows call control.focus();
    // forward it to the actual text field so keyboard/AT focus lands on the
    // input, not the host wrapper.
    focus() {
      if (this._input) this._input.focus();
    }

    _proxyAttribute(name, value) {
      if (!this._input) return;
      if (value == null) this._input.removeAttribute(name);
      else this._input.setAttribute(name, value);
    }

    _render() {
      this.classList.add('nl-phone-input');

      var input = document.createElement('input');
      input.type = 'tel';
      input.className = 'nl-phone-input__field';

      this.appendChild(input);

      this._input = input;
    }

    _bind() {
      var self = this;
      this._input.addEventListener('input', function (event) {
        event.stopPropagation();
        self._syncFromInput();
      });
      this._input.addEventListener('change', function (event) {
        event.stopPropagation();
        self._syncFromInput();
        self._canonicalize();
      });
      this._input.addEventListener('blur', function () {
        self._canonicalize();
      });
    }

    // Tidy the visible text to the stored 07… form, but only once the seller
    // has left the field. Doing it per keystroke is what used to eat a typed
    // "+44": the moment the field held exactly "+44" the prefix was stripped
    // to nothing, and the remaining digits then arrived in an empty field
    // where no country code was left to trigger the leading 0.
    _canonicalize() {
      if (!this._input) return;
      var normalized = this._normalize(this._input.value);
      if (this._input.value !== normalized) this._input.value = normalized;
    }

    // Store the UK national form while accepting +44, 0044 and bare 44 values,
    // however they arrive — typed a character at a time, pasted, or autofilled.
    // Server-side validation accepts UK mobiles and landlines (listing-submission
    // and UserCreationUtils both enforce it), so this only rescues numbers that
    // were always meant to pass, never widens what counts as valid.
    //
    // The country code is stripped only when a national number actually
    // follows it, so a field holding just "+44" mid-type is left alone rather
    // than emptied. Spaces are preserved — every validator and the server's
    // sanitizeListingForm strip them before use.
    _normalize(raw) {
      var s = String(raw == null ? '' : raw).trim();
      var rest = s.replace(/^(?:\+44|0044|44)(?=[\s.\-()]*[1-9])/, '');
      if (rest === s) return s;
      return '0' + rest.replace(/^[\s.\-()]+/, '');
    }

    // Report the normalised value upward without touching the field's text;
    // the seller keeps whatever they are typing, and _canonicalize tidies it
    // when they leave.
    _syncFromInput() {
      var normalized = this._normalize(this._input.value);
      var changed = normalized !== this._value;
      this._value = normalized;
      if (changed) {
        this.dispatchEvent(new Event('input', { bubbles: true }));
        this.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  window.customElements.define('nl-phone-input', NlPhoneInput);
})();
